// Insight filtering thresholds
export const MIN_IMPACT_PERCENTAGE = 10; // Minimum percentage contribution to show as insight
export const MAX_INSIGHTS = 10; // Max insights to show per section
export const HIGH_IMPACT_THRESHOLD = 30; // Percentage threshold to consider an insight as high impact

/**
 * Creates insights from grouping data based on selected filters
 * @param {Object} groupingMaps - The grouped data (card, procedure, hospital, physician)
 * @param {Array} allSections - Array of selected sections to include
 * @param {Array} allActivities - Selected activity filter ('all', 'run', 'restock', etc.)
 * @returns {Array} Array of insight objects sorted by percentage
 */
export function createInsights(groupingMaps, allSections, allActivities) {
    if (!groupingMaps) return [];

    const allInsights = {};
    const sections = allSections;

    allActivities.forEach(activity => {
        if (activity === 'all') return;
        const allInsightsForActivity = [];

        sections.forEach(section => {
            const groupData = groupingMaps[section];
            if (!groupData || Object.keys(groupData).length === 0) return;

            // Calculate total value for the section
            const groupValues = Object.values(groupData);
            const totalValue = calculateSectionTotal(groupValues, activity);

            if (totalValue === 0) return;

            // Sort groups by their contribution and get top contributors
            const sortedGroups = groupValues
                .map(groupValue => processGroup(groupValue, activity, totalValue))
                .filter(groupValue => groupValue.percentage >= MIN_IMPACT_PERCENTAGE)
                .sort((a, b) => b.percentage - a.percentage);

            // Create insights for this section
            sortedGroups.forEach((groupValue, index) => {
                allInsightsForActivity.push(createInsightObject(groupValue, section, index, activity));
            });
        });

        allInsights[activity] = allInsightsForActivity;
    });

    return allInsights;
}

/**
 * Gets insights for a specific activity, sorted by percentage
 * @param {Object} activity - Activity filter ('all', 'run', 'restock', etc.)
 * @param {Array} allInsights - All generated insights for all activities
 * @returns {Array} Filtered and sorted insights for the selected activity
 */
export function getInsightsForActivity(activity, allInsights) {
    // Sort all insights by percentage descending and add overall rank
    const insights = activity === 'all' ? Object.values(allInsights).flat() : allInsights[activity] || [];

    return insights
        .sort((a, b) => b.percentage - a.percentage)
        .filter((_, index) => index < MAX_INSIGHTS) // Limit total insights
        .map((insight, index) => ({ ...insight, overallRank: index + 1 }));
}

/**
 * Calculates the total value for a section based on chart view
 * @param {Array} groups - Array of group data
 * @param {string} chartView - Current chart view
 * @returns {number} Total value for the section
 */
function calculateSectionTotal(groupValues, activity) {
    return groupValues.reduce((sum, group) => {
        let activityValue;
        switch (activity) {
        case 'run':
            activityValue = group.run_quantity || 0;
            break;
        case 'restock':
            activityValue = group.restock_quantity || 0;
            break;
        case 'trashed':
            activityValue = group.trashed_quantity || 0;
            break;
        case 'used':
            activityValue = group.used_quantity || 0;
            break;
        case 'picked':
            activityValue = group.picked_quantity || 0;
            break;
        case 'all':
        default:
            activityValue = (group.run_quantity || 0) + (group.restock_quantity || 0) + (group.trashed_quantity || 0) +
                (group.picked_quantity || 0) + (group.used_quantity || 0);
            break;
        }
        return sum + activityValue;
    }, 0);
}

/**
 * Processes a single group to determine its activity values and percentages
 * @param {Object} groupValue - Individual group value data ex: one physician's counts
 * @param {string} activity - Selected activity filter
 * @param {number} totalValue - Total value for percentage calculation
 * @returns {Object} Processed group with calculated values
 */
function processGroup(groupValue, activity, totalValue) {
    let value, activityType, activityValue;
    
    // Show specific activity value
    const activityKey = `${activity}_quantity`;
    value = groupValue[activityKey] || 0;
    activityType = activity;
    activityValue = value;
    
    const percentage = (value / totalValue) * 100;
    return { 
        ...groupValue, 
        value, 
        percentage, 
        activityType, 
        activityValue,
        displayValue: activityValue
    };
}

/**
 * Creates an insight object from processed group data
 * @param {Object} group - Processed group data
 * @param {string} section - Section name (card, procedure, etc.)
 * @param {number} index - Index within section
 * @param {string} activity - activity filter
 * @returns {Object} Insight object
 */
function createInsightObject(group, section, index, activity) {
    return {
        id: `${section}-${group.name}-${index}-${activity}`,
        title: group.name,
        value: group.activityValue,
        totalValue: group.value,
        percentage: group.percentage,
        section,
        rank: index + 1,
        sectionRank: index + 1,
        isHighImpact: group.percentage >= HIGH_IMPACT_THRESHOLD,
        activityType: group.activityType
    };
}

/**
 * Groups insights by section for display purposes
 * @param {Array} insights - Array of insight objects
 * @param {Array} selectedSections - Array of selected sections
 * @returns {Object} Insights grouped by section
 */
export function groupInsightsBySection(insights, selectedSections) {
    return selectedSections.reduce((acc, section) => {
        acc[section] = insights.filter(insight => insight.section === section);
        return acc;
    }, {});
}